import { useGlobalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useRef } from "react";
import { ScrollView, Sheet, XStack, YStack } from "tamagui";

import { ConsentType } from "@/api/generated/models";
import { useUpdateConsents } from "@/api/mutations/";
import { useGetUserProfile } from "@/api/queries/userProfile";
import { Dialog } from "@/components/Dialog";
import ToggleSwitch from "@/components/forms/ToggleSwitch";
import Logo from "@/components/Logo";
import { H3, H4, Link, Paragraph, Text } from "@/components/Typography";
import { brandName, medicalGroupAbbr, medicalGroupName } from "@/config/brand";
import { useIsSignedInStable } from "@/hooks/useIsSignedInStable";

const Content = ({
  email,
  onEnableDataSharing,
  isUpdating,
}: {
  email: string | undefined;
  onEnableDataSharing: () => void;
  isUpdating: boolean;
}) => (
  <YStack
    paddingHorizontal="$200"
    paddingTop="$800"
    paddingBottom="$800"
    $lg={{ padding: "$400" }}
    gap="$300"
  >
    <XStack justifyContent="center">
      <Logo.Only size={28} />
    </XStack>

    <YStack gap="$100">
      <H3>Data sharing is turned off</H3>
      <Text color="$textSecondary">
        You can either turn data sharing back on to continue using the platform,
        or request your medical records via email.
      </Text>
    </YStack>

    <YStack gap="$100">
      <H4>Turn data sharing on to continue usage</H4>
      <ToggleSwitch
        label={`Data sharing between ${brandName} and ${medicalGroupName} (${medicalGroupAbbr})`}
        checked={false}
        onCheckedChange={(enabled) => {
          if (enabled) {
            onEnableDataSharing();
          }
        }}
        disabled={isUpdating}
      />
    </YStack>

    <YStack gap="$100">
      <H4>Request your medical records</H4>
      <Paragraph size="$sm">
        Email{" "}
        <Link size="$sm" bold href="mailto:support@curaihealth.com" external>
          support@curaihealth.com
        </Link>{" "}
        and request to have your medical records exported and emailed to you.
      </Paragraph>
    </YStack>
  </YStack>
);

export default function DataSharingRevokedOverlay() {
  const router = useRouter();
  const { settings: activeSettingsTab } = useGlobalSearchParams();
  const isSignedIn = Boolean(useIsSignedInStable());
  const { data: userProfile } = useGetUserProfile({ enabled: isSignedIn });
  const { mutate: updateConsents, isPending: isUpdating } = useUpdateConsents();

  const dataSharingRevoked =
    userProfile?.consents?.[ConsentType.data_sharing] === false;

  const pendingNavToSettings = useRef(false);

  // If consent is revoked while another dialog (e.g. Settings) is open, close it
  // so this overlay is not hidden behind it.
  useEffect(() => {
    if (dataSharingRevoked && activeSettingsTab) {
      router.setParams({ settings: undefined });
    }
  }, [dataSharingRevoked, activeSettingsTab, router]);

  // Navigate to data-sharing settings once the profile reflects re-enabled consent.
  // This defers navigation until after dataSharingRevoked transitions to false,
  // avoiding a race with the clearing effect above.
  useEffect(() => {
    if (!dataSharingRevoked && pendingNavToSettings.current) {
      pendingNavToSettings.current = false;
      router.setParams({ settings: "data-sharing" });
    }
  }, [dataSharingRevoked, router]);

  const handleOverlayPress = (event: any) => {
    event.stopPropagation();
    event.preventDefault?.();
    return false;
  };

  const handleEnableDataSharing = useCallback(() => {
    updateConsents(
      {
        data: {
          consents: {
            [ConsentType.data_sharing]: true,
          },
          source: "data-sharing-revoked-overlay",
        },
      },
      {
        onSuccess: () => {
          pendingNavToSettings.current = true;
        },
      },
    );
  }, [updateConsents]);

  if (!dataSharingRevoked) {
    return null;
  }

  return (
    <Dialog open={true} modal disableRemoveScroll>
      <Dialog.Adapt when="maxLg">
        <Sheet
          modal
          open={true}
          disableDrag
          dismissOnSnapToBottom={false}
          snapPoints={[98]}
        >
          <Sheet.Frame flex={1}>
            <ScrollView flex={1}>
              <Content
                email={userProfile?.email}
                onEnableDataSharing={handleEnableDataSharing}
                isUpdating={isUpdating}
              />
            </ScrollView>
          </Sheet.Frame>
          <Sheet.Overlay
            backgroundColor="$fillOverlay"
            backdropFilter="blur(8px)"
            onPress={handleOverlayPress}
          />
        </Sheet>
      </Dialog.Adapt>
      <Dialog.Portal>
        <Dialog.Overlay
          backgroundColor="$fillOverlay"
          backdropFilter="blur(8px)"
          onPress={handleOverlayPress}
        />
        <Dialog.FocusScope trapped loop>
          <Dialog.Content
            $lg={{
              maxWidth: "$constraint",
              width: "100%",
              minWidth: 500,
            }}
            minWidth="100%"
          >
            <Content
              email={userProfile?.email}
              onEnableDataSharing={handleEnableDataSharing}
              isUpdating={isUpdating}
            />
          </Dialog.Content>
        </Dialog.FocusScope>
      </Dialog.Portal>
    </Dialog>
  );
}
